import { useEffect, useMemo, useRef, useState } from "react";
import { FaWhatsapp, FaEnvelope, FaSms } from "react-icons/fa";
import {
  buildClientWhatsAppUrl,
  buildNotifyContextFromConfirmation,
  buildSmsHref,
  copyBankNotifyMessage,
  getAdminFromEmail,
  getBankTracedShareMessage,
  getBankTracedShareSubject,
  listBankerNotifyRecipients,
  openGmailCompose,
  openSmsComposer,
  openWhatsAppUrl,
  sendConfirmationBankNotify,
} from "../../../utils/bankNotifyShare";
import { repoCaseService } from "../../../services/repoCase.service";
import { getApiErrorMessage } from "../../../utils/downloadExcel";
import { resolveConfirmationCaseId, applyTracedVehicleToCase } from "./confirmationListUtils";
import "../../../styles/vehicles.css";

const MAX_RECIPIENTS = 4;

function RecipientDropdown({
  open,
  options,
  selectedIds,
  busy,
  sending,
  sendIcon,
  ariaLabel,
  onToggle,
  onSend,
}) {
  if (!open || !options.length) return null;
  return (
    <div className="fv-whatsapp-menu__dropdown" role="menu" aria-label={ariaLabel}>
      <div className="fv-whatsapp-menu__header">
        <span className="fv-whatsapp-menu__header-title">Select recipients</span>
        <span className="fv-whatsapp-menu__header-count">
          {selectedIds.length}/{MAX_RECIPIENTS}
        </span>
      </div>

      <div className="fv-whatsapp-menu__list">
        {options.map((opt) => {
          const checked = selectedIds.includes(opt.id);
          const maxReached = selectedIds.length >= MAX_RECIPIENTS;
          return (
            <label
              key={`${ariaLabel}-${opt.id}-${opt.phone}`}
              className={`fv-whatsapp-menu__checkbox-item${
                checked ? " fv-whatsapp-menu__checkbox-item--selected" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={busy || (!checked && maxReached)}
                onChange={() => onToggle(opt)}
                aria-label={`Select ${opt.label}`}
              />
              <span className="fv-whatsapp-menu__checkbox-content">
                <span className="fv-whatsapp-menu__item-label">
                  <span>{opt.label}</span>
                </span>
                <span className="fv-whatsapp-menu__item-role">{opt.roleLabel}</span>
                <span className="fv-whatsapp-menu__item-phone">{opt.phone}</span>
              </span>
            </label>
          );
        })}
      </div>

      {selectedIds.length >= MAX_RECIPIENTS ? (
        <div className="fv-whatsapp-menu__limit">
          Maximum {MAX_RECIPIENTS} recipients can be selected.
        </div>
      ) : null}

      <button
        type="button"
        className="fv-whatsapp-menu__send"
        disabled={busy || selectedIds.length === 0}
        onClick={onSend}
      >
        {sendIcon}
        <span>
          {sending
            ? "Sending…"
            : selectedIds.length === 0
              ? "Select recipients"
              : `Send to ${selectedIds.length} ${
                  selectedIds.length === 1 ? "recipient" : "recipients"
                }`}
        </span>
      </button>
    </div>
  );
}

export default function NotifyBankActions({
  caseData,
  confirmation,
  token,
  authUser,
  disabled = false,
  buttonClassPrefix = "lrms-notify",
}) {
  const [apiOptions, setApiOptions] = useState([]);
  const [previewMessage, setPreviewMessage] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [notifyStatus, setNotifyStatus] = useState("");
  const [notifyPhase, setNotifyPhase] = useState("");
  const [notifyChannel, setNotifyChannel] = useState("");
  const [notifySending, setNotifySending] = useState(false);
  const [manualToEmail, setManualToEmail] = useState("");
  const notifyLockRef = useRef(false);
  const menuRef = useRef(null);

  const displayCase = useMemo(
    () => applyTracedVehicleToCase(caseData, confirmation),
    [caseData, confirmation]
  );

  const recipients = useMemo(
    () => listBankerNotifyRecipients(displayCase, apiOptions),
    [displayCase, apiOptions]
  );

  useEffect(() => {
    const saved = recipients.find((row) => String(row.email || "").includes("@"));
    if (saved?.email && !manualToEmail) setManualToEmail(String(saved.email).trim());
  }, [recipients, manualToEmail]);

  const phoneOptions = useMemo(
    () =>
      recipients
        .filter((row) => row.phone)
        .map((row) => ({
          id: String(row.id),
          label: `Banker - ${row.name || row.label}`,
          name: row.name || row.label,
          phone: row.phone,
          email: row.email || "",
          roleLabel: "Banker",
        })),
    [recipients]
  );

  const caseId = resolveConfirmationCaseId(confirmation) || caseData?._id || caseData?.id;

  useEffect(() => {
    if (!token || !caseData || !caseId) return;
    let cancelled = false;
    repoCaseService
      .fetchBankNotifyMessage(token, {
        caseId,
        searchItem: displayCase,
        confirmationId: confirmation?._id || confirmation?.id,
        adminName: authUser?.name,
        adminPhone: authUser?.phone,
      })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        setApiOptions(Array.isArray(data.recipientOptions) ? data.recipientOptions : []);
        if (data.message) setPreviewMessage(data.message);
        if (data.subject) setPreviewSubject(data.subject);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, caseId, confirmation?._id, confirmation?.id, authUser?.name, authUser?.phone]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selectedOptions = phoneOptions.filter((row) => selectedIds.includes(row.id));
  const selected = selectedOptions[0] || phoneOptions[0] || null;
  const btn = buttonClassPrefix;
  const busy = notifySending || disabled;

  const toggleRecipient = (option) => {
    if (busy) return;
    setSelectedIds((current) => {
      if (current.includes(option.id)) {
        return current.filter((id) => id !== option.id);
      }
      if (current.length >= MAX_RECIPIENTS) return current;
      return [...current, option.id];
    });
  };

  const notifyContext = () =>
    buildNotifyContextFromConfirmation(displayCase, confirmation, authUser);

  const notifyMessage = () =>
    previewMessage || getBankTracedShareMessage(displayCase, notifyContext());

  const notifySubject = () =>
    previewSubject || getBankTracedShareSubject(displayCase, notifyContext());

  const handleNotify = async (channel, bankers = selectedOptions) => {
    if (!caseData || !caseId || notifyLockRef.current) return;
    const targets = (bankers.length ? bankers : selectedOptions).filter((row) => row.phone);
    const primary = targets[0] || selected;
    if ((channel === "whatsapp" || channel === "sms") && !primary?.phone) {
      setNotifyPhase("failed");
      setNotifyStatus("Select a banker number first.");
      return;
    }
    if (channel === "email") {
      const toEmail = String(manualToEmail || "").trim();
      const hasSavedEmail = recipients.some((row) => String(row.email || "").includes("@"));
      if (!toEmail.includes("@") && !hasSavedEmail) {
        setOpenMenu("email");
        setNotifyPhase("failed");
        setNotifyStatus("Enter the banker email, then click Send email.");
        return;
      }
    }

    notifyLockRef.current = true;
    setNotifySending(true);
    setNotifyChannel(channel);
    const notifyEmails = [
      ...recipients.map((row) => String(row.email || "").trim()),
      String(manualToEmail || "").trim(),
    ].filter((value) => value.includes("@"));
    setNotifyPhase("sending");
    setNotifyStatus(
      channel === "whatsapp"
        ? "Opening WhatsApp…"
        : channel === "email"
          ? "Sending email with attachments…"
          : "Opening SMS…"
    );

    const message = notifyMessage();
    if (channel === "whatsapp") {
      copyBankNotifyMessage(message).catch(() => {});
      targets.forEach((row, index) => {
        const url = buildClientWhatsAppUrl(row.phone, message);
        if (index === 0) openWhatsAppUrl(url);
        else window.open(url, "_blank", "noopener,noreferrer");
      });
    }
    if (channel === "sms") {
      copyBankNotifyMessage(message).catch(() => {});
      targets.forEach((row, index) => {
        if (index === 0) openSmsComposer({ to: row.phone, body: message });
        else window.open(buildSmsHref({ to: row.phone, body: message }), "_blank");
      });
    }
    if (channel === "email") {
      copyBankNotifyMessage(message).catch(() => {});
    }

    try {
      const result = await sendConfirmationBankNotify({
        channel,
        caseId,
        caseData: displayCase,
        confirmation,
        token,
        authUser,
        notifyPhone: primary?.phone || "",
        notifyEmail: notifyEmails.join(", "),
        recipientId: primary?.id || "",
        previewMessage: message,
        skipClientOpen: channel === "whatsapp" || channel === "sms" || channel === "email",
      });
      setNotifyPhase("sent");
      setNotifyStatus(
        (channel === "whatsapp" || channel === "sms") && targets.length > 1
          ? `${channel === "sms" ? "SMS" : "WhatsApp"} opened for ${targets.length} bankers.`
          : result.message
      );
    } catch (err) {
      setNotifyPhase("failed");
      setNotifyStatus(await getApiErrorMessage(err, "Could not notify the selected banker."));
    } finally {
      notifyLockRef.current = false;
      setNotifySending(false);
    }
  };

  const handleChannelButtonClick = (channel) => {
    if (busy) return;
    if (!phoneOptions.length) {
      if (channel === "email") handleNotify("email");
      else handleNotify(channel);
      return;
    }
    setOpenMenu((current) => (current === channel ? null : channel));
  };

  const handleSendSelected = (channel) => {
    if (!selectedOptions.length) return;
    setOpenMenu(null);
    handleNotify(channel, selectedOptions);
  };

  return (
    <section className="lrms-section lrms-notify-section">
      <h2 className="lrms-section__title">Notify bank</h2>
      {notifyStatus ? (
        <p
          className={
            notifyPhase === "failed"
              ? "cfm-status cfm-status--failed"
              : notifyPhase === "sending"
                ? "cfm-status cfm-status--sending"
                : "cfm-status"
          }
          role="status"
        >
          {notifyPhase === "sent" ? "Sent. " : notifyPhase === "failed" ? "Failed. " : ""}
          {notifyStatus}
        </p>
      ) : null}

      {phoneOptions.length ? null : (
        <p className="lrms-section__hint">
          No banker mobile found on this case. WhatsApp/SMS need a 1st/2nd/3rd banker number from Excel.
        </p>
      )}

      <div className={`${btn}-btns lrms-notify-btns`} ref={menuRef}>
        <div className="fv-whatsapp-menu lrms-notify-wa-menu">
          <button
            type="button"
            className={`${btn}-btn ${btn}-btn--wa lrms-notify-btn lrms-notify-btn--wa`}
            disabled={!caseData || busy}
            aria-haspopup={phoneOptions.length ? "menu" : undefined}
            aria-expanded={phoneOptions.length ? openMenu === "whatsapp" : undefined}
            onClick={() => handleChannelButtonClick("whatsapp")}
          >
            <FaWhatsapp aria-hidden />
            <span>{notifySending && notifyChannel === "whatsapp" ? "Sending…" : "WhatsApp"}</span>
            {phoneOptions.length ? (
              <span className="fv-whatsapp-menu__caret" aria-hidden>
                ▾
              </span>
            ) : null}
          </button>
          <RecipientDropdown
            open={openMenu === "whatsapp"}
            options={phoneOptions}
            selectedIds={selectedIds}
            busy={busy}
            sending={notifySending && notifyChannel === "whatsapp"}
            sendIcon={<FaWhatsapp />}
            ariaLabel="WhatsApp recipients"
            onToggle={toggleRecipient}
            onSend={() => handleSendSelected("whatsapp")}
          />
        </div>

        <div className="fv-whatsapp-menu lrms-notify-wa-menu">
          <button
            type="button"
            className={`${btn}-btn ${btn}-btn--sms lrms-notify-btn lrms-notify-btn--sms`}
            disabled={!caseData || busy}
            aria-haspopup={phoneOptions.length ? "menu" : undefined}
            aria-expanded={phoneOptions.length ? openMenu === "sms" : undefined}
            onClick={() => handleChannelButtonClick("sms")}
          >
            <FaSms aria-hidden />
            <span>{notifySending && notifyChannel === "sms" ? "Sending…" : "SMS"}</span>
            {phoneOptions.length ? (
              <span className="fv-whatsapp-menu__caret" aria-hidden>
                ▾
              </span>
            ) : null}
          </button>
          <RecipientDropdown
            open={openMenu === "sms"}
            options={phoneOptions}
            selectedIds={selectedIds}
            busy={busy}
            sending={notifySending && notifyChannel === "sms"}
            sendIcon={<FaSms />}
            ariaLabel="SMS recipients"
            onToggle={toggleRecipient}
            onSend={() => handleSendSelected("sms")}
          />
        </div>

        <div className="fv-whatsapp-menu lrms-notify-wa-menu">
          <button
            type="button"
            className={`${btn}-btn ${btn}-btn--email lrms-notify-btn lrms-notify-btn--email`}
            disabled={!caseData || busy}
            aria-haspopup="menu"
            aria-expanded={openMenu === "email"}
            onClick={() => setOpenMenu((current) => (current === "email" ? null : "email"))}
          >
            <FaEnvelope aria-hidden />{" "}
            {notifySending && notifyChannel === "email" ? "Sending…" : "Email"}
            <span className="fv-whatsapp-menu__caret" aria-hidden>
              ▾
            </span>
          </button>
          {openMenu === "email" ? (
            <div className="fv-whatsapp-menu__dropdown" role="menu" aria-label="Email recipient">
              <div className="fv-whatsapp-menu__header">
                <span className="fv-whatsapp-menu__header-title">Banker email</span>
              </div>
              <label className="lrms-notify-email-field">
                <span className="sr-only">Banker email</span>
                <input
                  type="email"
                  value={manualToEmail}
                  onChange={(e) => setManualToEmail(e.target.value)}
                  placeholder="Enter banker email"
                  disabled={busy}
                  autoComplete="off"
                />
              </label>
              <p className="lrms-section__hint">
              The email will include the Front, Side, and Back photos, along with the Crane Bill.
              </p>
              <button
                type="button"
                className="fv-whatsapp-menu__send"
                disabled={busy || !String(manualToEmail || "").includes("@")}
                onClick={() => {
                  setOpenMenu(null);
                  handleNotify("email");
                }}
              >
                <FaEnvelope />
                <span>{notifySending && notifyChannel === "email" ? "Sending…" : "Send email"}</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
